import { ChangeEvent, Component } from "react";
import {
    createStyles,
    Grid,
    MenuItem,
    TextField,
    Theme,
    Typography,
    WithStyles,
    withStyles
} from "@material-ui/core";
import { CountryRegionData } from "react-country-region-selector";
import InputMask from "react-input-mask";

interface IComponentProps {
    phoneNumber: string;
    address1: string;
    address2: string;
    country: string;
    province: string;
    city: string;
    postalCode: string;
    isViewMode: boolean;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

type ProfileInformationProps = WithStyles<typeof styles> & IComponentProps;

interface IProfileInformationState {
    phoneNumber: string;
    address1: string;
    address2: string;
    country: string;
    province: string;
    city: string;
    postalCode: string;
}

const WhiteTextTypography = withStyles({
    root: {
        color: "#FFFFFF"
    }
})(Typography) as typeof Typography;

const styles = (theme: Theme) =>
    createStyles({
        form: {
            maxWidth: 1000,
            width: "100%"
        },
        formFieldTop: { marginTop: theme.spacing(4) },
        postalCodeField: { textTransform: "uppercase", color: "white" },
        sectionHeaderParagraph: {
            borderBottom: `4px solid ${theme.palette.primary.main}`,
            marginTop: theme.spacing(4)
        },
        selectField: {
            marginTop: theme.spacing(5),
            width: "100%",
            color: "white"
        },
        input: {
            color: "white"
        },
        floatingLabel: {
            color: "#CCCCCC"
        }
    });

class ProfileInformation extends Component<
    ProfileInformationProps,
    IProfileInformationState
> {
    constructor(props: ProfileInformationProps) {
        super(props);

        this.state = {
            address1: "",
            address2: "",
            city: "",
            country: "CA",
            phoneNumber: "",
            postalCode: "",
            province: ""
        };
    }

    public componentDidUpdate(prevProps: ProfileInformationProps) {
        if (prevProps.address1 !== this.props.address1) {
            this.setState({
                address1: this.props.address1 || "",
                address2: this.props.address2 || "",
                city: this.props.city || "",
                country: this.props.country || "CA",
                phoneNumber: this.props.phoneNumber || "",
                postalCode: this.props.postalCode || "",
                province: this.props.province || ""
            });
        }
    }

    public render() {
        const classes = this.props.classes;
        return (
            <div className={classes.form}>
                <WhiteTextTypography
                    className={classes.sectionHeaderParagraph}
                    component="p"
                    variant="h4"
                >
                    Profile Information
                </WhiteTextTypography>
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        <TextField
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            label="Address"
                            name="address1"
                            value={this.state.address1}
                            disabled={this.props.isViewMode}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        />
                    </Grid>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        <TextField
                            className={classes.formFieldTop}
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            name="address2"
                            value={this.state.address2}
                            disabled={this.props.isViewMode}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        <TextField
                            className={classes.selectField}
                            required={true}
                            name="country"
                            label="Country"
                            value={this.state.country}
                            select={true}
                            disabled={this.props.isViewMode}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        >
                            {CountryRegionData.map((e) => (
                                <MenuItem key={e[1]} value={e[1]}>
                                    {e[0]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        {this.IsInCanadaOrUS() && (
                            <TextField
                                className={classes.selectField}
                                required={true}
                                name="province"
                                label="Province/State"
                                value={this.state.province}
                                select={true}
                                disabled={this.props.isViewMode}
                                onChange={this.onChange}
                                InputProps={{
                                    className: classes.input
                                }}
                                InputLabelProps={{
                                    className: classes.floatingLabel
                                }}
                            >
                                {this.getRegions(CountryRegionData).map(
                                    (option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        )}
                        {!this.IsInCanadaOrUS() && (
                            <TextField
                                margin="normal"
                                fullWidth={true}
                                required={true}
                                name="province"
                                label="Province/State"
                                value={this.state.province}
                                disabled={this.props.isViewMode}
                                onChange={this.onChange}
                                InputProps={{
                                    className: classes.input
                                }}
                                InputLabelProps={{
                                    className: classes.floatingLabel
                                }}
                            />
                        )}
                    </Grid>
                </Grid>
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        <TextField
                            margin="normal"
                            fullWidth={true}
                            required={true}
                            label="Town/City"
                            name="city"
                            value={this.state.city}
                            disabled={this.props.isViewMode}
                            onChange={this.onChange}
                            InputProps={{
                                className: classes.input
                            }}
                            InputLabelProps={{
                                className: classes.floatingLabel
                            }}
                        />
                    </Grid>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        {this.IsInCanadaOrUS() && (
                            <InputMask
                                mask={this.IsInCanada() ? "a9a 9a9" : "99999"}
                                value={this.state.postalCode}
                                disabled={this.props.isViewMode}
                                onChange={this.onChange}
                            >
                                {() => (
                                    <TextField
                                        margin="normal"
                                        fullWidth={true}
                                        required={true}
                                        label="Postal Code/Zip"
                                        name="postalCode"
                                        disabled={this.props.isViewMode}
                                        inputProps={{
                                            className: classes.postalCodeField
                                        }}
                                        InputLabelProps={{
                                            className: classes.floatingLabel
                                        }}
                                    />
                                )}
                            </InputMask>
                        )}
                        {!this.IsInCanadaOrUS() && (
                            <TextField
                                margin="normal"
                                fullWidth={true}
                                required={true}
                                label="Postal Code/Zip"
                                name="postalCode"
                                value={this.state.postalCode}
                                disabled={this.props.isViewMode}
                                onChange={this.onChange}
                                inputProps={{
                                    className: classes.postalCodeField
                                }}
                                InputLabelProps={{
                                    className: classes.floatingLabel
                                }}
                            />
                        )}
                    </Grid>
                </Grid>
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={12} sm={12} md={6}>
                        <InputMask
                            mask="(999) 999-9999"
                            value={this.state.phoneNumber}
                            disabled={this.props.isViewMode}
                            onChange={this.onChange}
                        >
                            {() => (
                                <TextField
                                    id="phoneNumber"
                                    label="Phone Number"
                                    name="phoneNumber"
                                    margin="normal"
                                    type="tel"
                                    fullWidth={true}
                                    required={true}
                                    disabled={this.props.isViewMode}
                                    InputProps={{
                                        className: classes.input
                                    }}
                                    InputLabelProps={{
                                        className: classes.floatingLabel
                                    }}
                                />
                            )}
                        </InputMask>
                    </Grid>
                </Grid>
            </div>
        );
    }

    private IsInCanadaOrUS() {
        return this.state.country === "CA" || this.state.country === "US";
    }

    private IsInCanada() {
        return this.state.country === "CA";
    }

    private getRegions = (countries: string[][]) => {
        if (!this.state.country) {
            return [];
        }
        const selectedCountry = this.state.country;
        const country = countries.find(
            (element) => element[1] === selectedCountry
        );

        if (!country) {
            return [];
        }

        return country[2].split("|").map((regionPair) => {
            const [regionName] = regionPair.split("~");
            return regionName;
        });
    };

    private onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value as string;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.setState({ [name]: value } as any);
        // Reset province on country change
        if (name === "country") {
            this.setState({ province: "" });
        }

        this.props.onChange(event);
    };
}

export default withStyles(styles)(ProfileInformation);
